import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { IAlertConfig } from '@shared/models/alert';
import { EmpresaService } from '@shared/service/app/empresa.service';
import { FormateadorService } from '@shared/service/app/formateador.service';
import { Subject, takeUntil } from 'rxjs';
import * as XLSX from 'xlsx';
import * as Papa from 'papaparse';
import { IAlert, IFile } from './file';
import { AppAlertService } from '@shared/service/app/alert.service';


@Component({
  selector: 'app-files-manager',
  templateUrl: './files-manager.component.html',
  styleUrls: ['./files-manager.component.scss']
})
export class FilesManagerComponent implements OnInit {

  @Input() validExtension: any[] = ['.xlsx', '.csv', '.jpg', '.png', '.pdf'];
  @Input() maxSize: number = 100;

  @Output() dataChange = new EventEmitter<any[]>();
  @Output() alertEmitter = new EventEmitter<IAlert>();

  showAlert = false;
  _alertConfig: IAlertConfig;
  color_primario: string;
  color_secundario: string;
  default_color: string = 'bg-primary';
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  protected fileAdjuntoImagen: any;
  // protected validExtension = ['.jpg', '.png', '.pdf'];
  archivo: File | null = null;
  selectedFiles: File[] = [];

  adjuntos: IFile[] = [];
  text_valid_extension: string;

  constructor(
    private _empresaService: EmpresaService,
    private _formateadorService: FormateadorService,
    public alert: AppAlertService
  ) {
    // Subscribe to empresa data
    this._empresaService.empresa$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((empresa) => {
        this.color_primario = empresa.color_primario;
        this.color_secundario = empresa.color_secundario;
      });

    this._empresaService.getEmpresa();
  }

  ngOnInit() {
    // Convierte ['.xlsx', '.csv'] => '.xlsx,.csv'
    this.text_valid_extension = this.validExtension.join(',');
  }

  onValueChange() {
    this.dataChange.emit(this.adjuntos)
  }

  onAlert(alerta: IAlert) {
    this.alertEmitter.emit(alerta)
  }

  procesarArchivo($event: any) {
    this.archivo = $event.target.files[0];

    if (!this.archivo) return console.log('No ha seleccionado un archivo');

    const archivo_data = ($event.target as HTMLInputElement).files[0];

    let size = Math.round(archivo_data.size / 1024); // Convierto el tamano de la imagen a kilobytes
    let nombre_archivo = archivo_data.name; // Encapsulo el nombre del archivo
    let extension_archivo = this._formateadorService.obtenerExtension(nombre_archivo); // Obtengo la extension del archivo
    nombre_archivo = this._formateadorService.normalizarTexto(this._formateadorService.removeExtension(nombre_archivo));

    // Valido tamaño del archivo
    if (size > this.maxSize) {
      const alerta: IAlert = {
        type: 'info',
        mensaje: 'El tamaño del archivo es superior al tamaño permitido: ' + size + 'Mb'
      }
      return this.onAlert(alerta);
    }

    // Valido el formato del archivo
    if (!this.validExtension.includes('.' + extension_archivo)) {
      const alerta: IAlert = {
        type: 'info',
        mensaje: 'Formato de archivo no soportado: ' + '.' + extension_archivo
      }
      return this.onAlert(alerta);
    }

    // Valido que no exista ya el archivo
    if (this.adjuntos.find(item => item.path === nombre_archivo)) {
      const alerta: IAlert = {
        type: 'info',
        mensaje: 'Ya existe un archivo con este path: ' + nombre_archivo + '.' + extension_archivo
      }
      return this.onAlert(alerta);
    }

    switch (extension_archivo) {
      case "xlsx":
        this.readXlsx(nombre_archivo, size, extension_archivo);
        break;
      case "csv":
        this.readCsv(nombre_archivo, size, extension_archivo);
        break;
      case "jpg":
        this.setImagen(archivo_data, nombre_archivo, size, extension_archivo);
        break;
      case "png":
        this.setImagen(archivo_data, nombre_archivo, size, extension_archivo);
        break;
    }
  }


  setImagen(imagen_data: any, nombre_archivo: string, size: number, extension_archivo: string) {

    this.getBase64(imagen_data).then((imagen_en_base_64) => {

      // Encapsulo los valores que me interesan de la imagen
      let data: IFile = {
        base64: imagen_en_base_64.toString().split('base64,')[1],
        path: nombre_archivo,
        size: size,
        extension: extension_archivo,
      };

      // Guardo la informacion en una variable global
      this.adjuntos = [data, ...this.adjuntos];

      this.onValueChange();

    });
  }

  readXlsx(nombre_archivo: string, size: number, extension_archivo: string): void {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const bstr: string = e.target.result;
      const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' });

      // Tomamos la primera hoja
      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];

      // Convertimos a JSON
      this.selectedFiles = XLSX.utils.sheet_to_json(ws, { defval: null });

      if (this.selectedFiles.length === 0) {
        console.warn('⚠️ El XLSX está vacío o no se pudo procesar.');
        return;
      }

      // Obtengo las columnas del XLSX
      let columnsName = Object.values(this.selectedFiles[0]).map(column => this._formateadorService.normalizarTexto(column));
      let datos = this.selectedFiles.filter((item, index) => index !== 0).map(item => Object.values(item));

      // Encapsulo los valores que me interesan de la imagen
      const data: IFile = {
        base64: '',
        path: nombre_archivo,
        size,
        extension: extension_archivo,
        datos: this.normalizarXlsx(columnsName, datos)
      };

      // Guardo la informacion en una variable global
      this.adjuntos = [data, ...this.adjuntos];

      this.onValueChange();

    };
    reader.readAsBinaryString(this.archivo);
  }

  readCsv(nombre_archivo: string, size: number, extension_archivo: string): void {
    const reader = new FileReader();

    reader.onload = (e: any) => {
      const csv = e.target.result;
      const parsed = Papa.parse(csv, {
        header: true,
        skipEmptyLines: true,
        delimiter: ";", // 👈 forzá punto y coma si tus CSV lo usan
        dynamicTyping: true // 🔹 interpreta números y decimales correctamente
      });

      this.selectedFiles = parsed.data;

      if (this.selectedFiles.length === 0) {
        console.warn('⚠️ El CSV está vacío o no se pudo procesar.');
        return;
      }

      // 🔹 Obtengo las columnas de la primera fila
      let columnsName = Object.keys(this.selectedFiles[0]).map(column => this._formateadorService.normalizarTexto(String(column)));

      // 🔹 Obtengo los datos (sin incluir la fila de encabezados)
      let datos = this.selectedFiles.map(item => Object.values(item).map(v => v === '' ? null : v));

      // 🔹 Armo el objeto final
      const data: IFile = {
        base64: '',
        path: nombre_archivo,
        size,
        extension: extension_archivo,
        datos: this.normalizarXlsx(columnsName, datos)
      };

      // 🔹 Guardo la información en una variable global
      this.adjuntos = [data, ...this.adjuntos];

      // 🔹 Emito cambios
      this.onValueChange();
    };

    // ✅ Leer CSV como texto
    reader.readAsText(this.archivo);
  }


  /**
   * Metodo parametrizado para eliminar los archivos multimedia segun el momento de persistencia
   * @param file Archivo a eliminar
   */
  quitarArchivo(file: IFile) {
    switch (file.extension) {
      // Eliminar imagenes secundarias dadas de alta en la operacion actual
      case 'xlsx':
        {
          this.adjuntos = this.adjuntos.filter((adjunto) => adjunto.path.toLowerCase() != file.path.toLowerCase());
        }

        this.onValueChange();
        break;

      case 'csv':
        {
          this.adjuntos = this.adjuntos.filter((adjunto) => adjunto.path.toLowerCase() != file.path.toLowerCase());
        }

        this.onValueChange();
        break;

      case 'jpg':
        {
          this.adjuntos = this.adjuntos.filter((adjunto) => adjunto.path.toLowerCase() != file.path.toLowerCase());
        }

        this.onValueChange();
        break;

      case 'png':
        {
          this.adjuntos = this.adjuntos.filter((adjunto) => adjunto.path.toLowerCase() != file.path.toLowerCase());
        }

        this.onValueChange();
        break;

      default: {
        console.error('Ocurrió un error al quitar el adjunto.');
      }
    }
  }

  private normalizarXlsx(columnsName: string[], items: any[]) {
    let datos = [];

    items.forEach((fila) => {
      let aux = {};

      columnsName.forEach((name, index) => {
        aux[name] = fila[index];
      })

      datos.push(aux);
    })

    return datos;
  }

  getFileSize(size: number) {
    return this._formateadorService.getFileSize(size);
  }

  getFileNameFromUrl(path: string) {
    return this._formateadorService.getFileNameFromUrl(path);
  }

  getFileTitle(file: File) {
    return this._formateadorService.getFileTitle(file);
  }

  async getBase64(file: any): Promise<any> {
    return this._formateadorService.getBase64(file);
  }
}
